import React, { useState, useCallback } from 'react';
import { reportsApi, type ReportKey } from '../../services/api';

function formatDateForInput(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const QUICK_RANGES: { label: string; getValue: () => [string, string] }[] = [
  { label: 'Today', getValue: () => { const t = new Date(); return [formatDateForInput(t), formatDateForInput(t)]; } },
  { label: 'Last 7 Days', getValue: () => { const end = new Date(); const start = new Date(end); start.setDate(start.getDate() - 6); return [formatDateForInput(start), formatDateForInput(end)]; } },
  { label: 'Last Month', getValue: () => { const end = new Date(); const start = new Date(end.getFullYear(), end.getMonth() - 1, 1); const last = new Date(end.getFullYear(), end.getMonth(), 0); return [formatDateForInput(start), formatDateForInput(last)]; } },
  { label: 'Last Quarter', getValue: () => { const end = new Date(); const q = Math.floor(end.getMonth() / 3) + 1; const start = new Date(end.getFullYear(), (q - 2) * 3, 1); const last = new Date(end.getFullYear(), (q - 1) * 3, 0); return [formatDateForInput(start), formatDateForInput(last)]; } },
  { label: 'Year to Date', getValue: () => { const end = new Date(); const start = new Date(end.getFullYear(), 0, 1); return [formatDateForInput(start), formatDateForInput(end)]; } },
  { label: 'Custom Range', getValue: () => { const end = new Date(); const start = new Date(end.getFullYear(), 0, 1); return [formatDateForInput(start), formatDateForInput(end)]; } },
];

const CATEGORIES: {
  title: string;
  icon: string;
  reports: { key: ReportKey; title: string; description: string }[];
}[] = [
  {
    title: 'Financial Reports',
    icon: 'dollar',
    reports: [
      { key: 'financial/profit-loss', title: 'Profit & Loss', description: 'Income statement for selected period' },
      { key: 'financial/balance-sheet', title: 'Balance Sheet', description: 'Assets, liabilities and equity' },
      { key: 'financial/cash-flow', title: 'Cash Flow Statement', description: 'Cash movement analysis' },
      { key: 'financial/trial-balance', title: 'Trial Balance', description: 'Account balances summary' },
      { key: 'financial/general-ledger', title: 'General Ledger', description: 'Detailed transaction history' },
    ],
  },
  {
    title: 'Accounts Receivable',
    icon: 'chart',
    reports: [
      { key: 'ar/aging-summary', title: 'AR Aging Summary', description: 'Outstanding invoices by age' },
      { key: 'ar/customer-statements', title: 'Customer Statements', description: 'Individual customer balances' },
      { key: 'ar/sales-by-customer', title: 'Sales by Customer', description: 'Revenue breakdown by customer' },
      { key: 'ar/invoice-register', title: 'Invoice Register', description: 'All invoices for period' },
    ],
  },
  {
    title: 'Accounts Payable',
    icon: 'card',
    reports: [
      { key: 'ap/aging-summary', title: 'AP Aging Summary', description: 'Outstanding bills by age' },
      { key: 'ap/vendor-statements', title: 'Vendor Statements', description: 'Individual vendor balances' },
      { key: 'ap/purchases-by-vendor', title: 'Purchases by Vendor', description: 'Spending breakdown by vendor' },
      { key: 'ap/bill-register', title: 'Bill Register', description: 'All bills for period' },
    ],
  },
  {
    title: 'Inventory Reports',
    icon: 'box',
    reports: [
      { key: 'inventory/valuation', title: 'Inventory Valuation', description: 'Current stock value' },
      { key: 'inventory/stock-status', title: 'Stock Status', description: 'Quantity on hand and reorder alerts' },
      { key: 'inventory/movement', title: 'Inventory Movement', description: 'Stock in/out history' },
    ],
  },
  {
    title: 'HR & Payroll',
    icon: 'person',
    reports: [
      { key: 'hr/employee-directory', title: 'Employee Directory', description: 'All active employees' },
      { key: 'hr/payroll-summary', title: 'Payroll Summary', description: 'Compensation breakdown' },
      { key: 'hr/department-headcount', title: 'Department Headcount', description: 'Staff by department' },
    ],
  },
];

const ICONS: Record<string, React.ReactNode> = {
  dollar: (
    <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  chart: (
    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
    </svg>
  ),
  card: (
    <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
  ),
  box: (
    <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
    </svg>
  ),
  person: (
    <svg className="w-6 h-6 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  ),
};

export default function ReportsPage() {
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setMonth(0);
    d.setDate(1);
    return formatDateForInput(d);
  });
  const [toDate, setToDate] = useState(formatDateForInput(new Date()));
  const [quickRange, setQuickRange] = useState('Year to Date');
  const [runningKey, setRunningKey] = useState<ReportKey | null>(null);
  const [exportingKey, setExportingKey] = useState<ReportKey | null>(null);
  const [previewData, setPreviewData] = useState<{ key: ReportKey; rows: unknown[] } | null>(null);

  const handleQuickRangeChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const label = e.target.value;
    setQuickRange(label);
    const option = QUICK_RANGES.find((r) => r.label === label);
    if (option) {
      const [from, to] = option.getValue();
      setFromDate(from);
      setToDate(to);
    }
  }, []);

  const runReport = useCallback(
    async (key: ReportKey) => {
      setRunningKey(key);
      setPreviewData(null);
      try {
        const res = await reportsApi.get(key, { from_date: fromDate, to_date: toDate });
        let rows: unknown[] = [];
        if (Array.isArray(res.data)) {
          rows = res.data;
        } else if (res.data && typeof res.data === 'object' && 'transactions' in (res.data as object)) {
          rows = (res.data as { transactions: unknown[] }).transactions ?? [];
        }
        setPreviewData({ key, rows });
      } catch (err) {
        console.error(err);
      } finally {
        setRunningKey(null);
      }
    },
    [fromDate, toDate]
  );

  const exportReport = useCallback(
    async (key: ReportKey) => {
      setExportingKey(key);
      try {
        await reportsApi.exportCsv(key, { from_date: fromDate, to_date: toDate });
      } catch (err) {
        console.error(err);
      } finally {
        setExportingKey(null);
      }
    },
    [fromDate, toDate]
  );

  return (
    <div className="h-full flex flex-col overflow-auto bg-gray-50">
      <div className="shrink-0 p-4 pb-2">
        <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
        <p className="text-sm text-gray-500 mt-0.5">Generate and analyze business reports</p>
      </div>

      <div className="shrink-0 px-3 sm:px-4 pb-4 flex flex-wrap items-center gap-3 sm:gap-4">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">From</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">To</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">Quick select</label>
          <select
            value={quickRange}
            onChange={handleQuickRangeChange}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500 min-w-[140px]"
          >
            {QUICK_RANGES.map((r) => (
              <option key={r.label} value={r.label}>
                {r.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex-1 px-3 sm:px-4 pb-6 space-y-6">
        {CATEGORIES.map((cat) => (
          <section key={cat.title} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-3">
              <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-gray-100">
                {ICONS[cat.icon] ?? ICONS.dollar}
              </span>
              <h2 className="text-lg font-semibold text-gray-900">{cat.title}</h2>
            </div>
            <ul className="divide-y divide-gray-100">
              {cat.reports.map((r) => (
                <li key={r.key} className="flex items-center justify-between gap-4 px-4 py-3 hover:bg-gray-50/80">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900">{r.title}</p>
                    <p className="text-sm text-gray-500">{r.description}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => runReport(r.key)}
                      disabled={runningKey !== null}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-brand-700 bg-brand-50 hover:bg-brand-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {runningKey === r.key ? (
                        <>
                          <span className="inline-block w-4 h-4 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
                          Running...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                          Run
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => exportReport(r.key)}
                      disabled={exportingKey !== null}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {exportingKey === r.key ? (
                        <>
                          <span className="inline-block w-4 h-4 border-2 border-gray-500 border-t-transparent rounded-full animate-spin" />
                          Exporting...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                          Export
                        </>
                      )}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>

      {previewData && previewData.rows.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setPreviewData(null)}>
          <div
            className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Report Preview</h3>
              <button
                type="button"
                onClick={() => setPreviewData(null)}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-gray-200">
                    {Object.keys(previewData.rows[0] as Record<string, unknown>).map((h) => (
                      <th key={h} className="text-left py-2 px-2 font-medium text-gray-700 capitalize">
                        {h.replace(/_/g, ' ')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewData.rows.slice(0, 100).map((row, i) => (
                    <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                      {Object.values(row as Record<string, unknown>).map((v, j) => (
                        <td key={j} className="py-2 px-2 text-gray-900">
                          {v != null ? String(v) : ''}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {previewData.rows.length > 100 && (
                <p className="text-xs text-gray-500 mt-2">Showing first 100 rows. Export for full data.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
